import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { calculateAgeFromDOB, calculateSizeTag } from '../utils/dogUtils';
import { ILS, formatRelativeDate } from '../utils/salesUtils';
import { CHECKOUT_MOODS } from '../utils/daycareUtils';
import AuditLabel from './AuditLabel';
import SignaturePad from './SignaturePad';
import MembershipCard from './MembershipCard';
import { useBathPackages } from '../hooks/useBathPackages';
import { useHappyBusPackages } from '../hooks/useHappyBusPackages';
import '../styles/components/dog-details-modal.css';
import '../styles/components/dog-form-enhancements.css';
import '../styles/components/dog-form.css';
import '../styles/pages/memberships.css';
import { CARTA_CLAUSES, CARTA_INTRO, cartaAsText } from '../constants/cartaResponsiva';


const API_URL = process.env.REACT_APP_API_BASE_URL || '';

export default function DogDetailsModal({ dog, isOpen, onClose, onEdit, onDelete, onDogUpdated }) {
    const { authorizedFetch } = useAuth();
    const navigate = useNavigate();
    const bathPkgHook = useBathPackages();
    const happyBusHook = useHappyBusPackages();
    
    const [activeTab, setActiveTab] = useState('info');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Data states
    const [dogData, setDogData] = useState(dog || {});
    const [daycareVisits, setDaycareVisits] = useState([]);
    const [daycarePackages, setDaycarePackages] = useState([]);
    const [hotelStays, setHotelStays] = useState([]);
    const [vetAppointments, setVetAppointments] = useState([]);
    const [groomingBookings, setGroomingBookings] = useState([]);
    const [dogSales, setDogSales] = useState([]);
    const [packageHistory, setPackageHistory] = useState([]);
    const [showAllDaycareVisits, setShowAllDaycareVisits] = useState(false);
    
    // Carta responsiva
    const [cartaSignature, setCartaSignature] = useState('');
    const [savingCarta, setSavingCarta] = useState('');

    // Membership
    const [dogMembership, setDogMembership]       = useState(null);
    const [membershipLoading, setMembershipLoading] = useState(false);
    const [membershipError, setMembershipError]     = useState('');
    const [newMemberStartDate, setNewMemberStartDate] = useState(() => {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const d = String(now.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    });
    const [creatingMembership, setCreatingMembership] = useState(false);

    // Bath packages
    const [bathPackages, setBathPackages] = useState([]);
    const [bathPkgBusy, setBathPkgBusy] = useState(false);
    const [bathPkgError, setBathPkgError] = useState('');
    const [newBathPkgDate, setNewBathPkgDate] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    });
    const [addBathDate, setAddBathDate] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    });

    // Happy Bus packages
    const [happyBusPackages, setHappyBusPackages] = useState([]);
    const [happyBusBusy, setHappyBusBusy] = useState(false);
    const [happyBusError, setHappyBusError] = useState('');
    const [newHappyBusPkgDate, setNewHappyBusPkgDate] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    });
    const [addTripDate, setAddTripDate] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    });

    // Lunch & Transportation form states
    const [lunchInstructions, setLunchInstructions] = useState('');
    const [transportation, setTransportation] = useState({
        needsTransportation: false,
        pickUpTime: '',
        dropOffTime: '',
        notes: ''
    });
    const [savingExtras, setSavingExtras] = useState(false);
    const [extrasSaved, setExtrasSaved] = useState('');

    useEffect(() => {
        if (isOpen && dog?._id) {
            setDogData(dog);
            setLunchInstructions(dog.lunchInstructions || '');
            setTransportation({
                needsTransportation: dog.transportation?.needsTransportation || false,
                pickUpTime: dog.transportation?.pickUpTime || '',
                dropOffTime: dog.transportation?.dropOffTime || '',
                notes: dog.transportation?.notes || ''
            });
            setShowAllDaycareVisits(false);
            fetchDogDetails(dog._id);
        }
    }, [isOpen, dog]);

    const fetchDogDetails = async (dogId) => {
        if (!dogId) {
            setError('Invalid dog ID');
            return;
        }
        
        setLoading(true);
        setError('');

        // Every section is independent, so they are fetched concurrently — the modal
        // waits for the slowest request instead of the sum of all of them.
        const getJson = async (path) => {
            const res = await authorizedFetch(`${API_URL}${path}`);
            if (!res.ok) throw new Error(`Error ${res.status}`);
            return res.json();
        };

        // Sections that may not be deployed yet stay graceful: they resolve to a
        // fallback instead of failing the whole load.
        const optional = (promise, fallback) => promise.catch(() => fallback);

        try {
            const [
                visits,
                dogPackages,
                bookings,
                vet,
                grooming,
                sales,
                pkgHistory,
                bathPkgs,
                busPkgs,
            ] = await Promise.all([
                getJson(`/api/daycare/visits/dog/${dogId}`),
                getJson(`/api/daycare/packages/dog/${dogId}`),
                getJson(`/api/hotel/dog/${dogId}`),
                getJson(`/api/vet/appointments/dog/${dogId}`),
                getJson(`/api/grooming/dog/${dogId}`),
                optional(getJson(`/api/sales?dogId=${dogId}`), []),
                optional(getJson(`/api/daycare/packages/history/dog/${dogId}`), []),
                optional(bathPkgHook.fetchForDog(dogId), []),
                optional(happyBusHook.fetchForDog(dogId), []),
                optional(fetchMembership(dogId), null),
            ]);

            setDaycareVisits(visits);
            setDaycarePackages(dogPackages);
            setHotelStays(bookings);
            setVetAppointments(vet);
            setGroomingBookings(grooming);
            setDogSales(Array.isArray(sales) ? sales : (sales.sales || []));
            setPackageHistory(pkgHistory);
            setBathPackages(bathPkgs);
            setHappyBusPackages(busPkgs);
        } catch (err) {
            console.error('Error fetching dog details:', err);
            setError(`Error al cargar la información del perro: ${err.message || 'Error desconocido'}`);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No date';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid date';
            
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return 'Invalid date';
        }
    };
    
    const formatDateOnly = (dateString) => {
        if (!dateString) return 'No date';

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid date';

            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return 'Invalid date';
        }
    };

    // UTC-safe version for dates stored as midnight UTC (e.g. daycare visit dates)
    const formatDateOnlyUTC = (dateString) => {
        if (!dateString) return 'No date';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid date';
            return date.toLocaleDateString('en-US', {
                year: 'numeric', month: 'short', day: 'numeric',
                timeZone: 'UTC'
            });
        } catch {
            return 'Invalid date';
        }
    };
    
    const formatTime = (dateString) => {
        if (!dateString) return 'No time';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid time';
            
            return date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return 'Invalid time';
        }
    };

    const saveExtras = async (field) => {
        if (!dogData?._id) return;
        setSavingExtras(true);
        setExtrasSaved('');
        try {
            const body = {};
            if (field === 'lunch') body.lunchInstructions = lunchInstructions;
            if (field === 'transportation') body.transportation = transportation;
            
            const res = await authorizedFetch(`${API_URL}/api/dogs/${dogData._id}/extras`, {
                method: 'PATCH',
                body: JSON.stringify(body)
            });
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Failed to save');
            }
            setExtrasSaved(field);
            setTimeout(() => setExtrasSaved(''), 2000);
        } catch (err) {
            console.error('Error saving extras:', err);
            setError(err.message);
        } finally {
            setSavingExtras(false);
        }
    };

    const saveCarta = async (sig) => {
        if (!dogData?._id || !sig) return;
        setSavingCarta('saving');
        try {
            const res = await authorizedFetch(`${API_URL}/api/dogs/${dogData._id}/extras`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ownerSignature: sig, signedAt: new Date().toISOString() })
            });
            if (!res.ok) throw new Error('Error al guardar la firma');
            const updated = await res.json();
            setDogData(updated);
            onDogUpdated?.(updated);
            setSavingCarta('saved');
            setTimeout(() => setSavingCarta(''), 2500);
        } catch (err) {
            console.error('Error saving carta:', err);
            setSavingCarta('error');
            setTimeout(() => setSavingCarta(''), 3000);
        }
    };

    // ── Membership helpers ──
    const membershipApi = async (path, options = {}) => {
        const res = await authorizedFetch(`${API_URL}/api/memberships${path}`, options);
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.error || `Error ${res.status}`);
        }
        return res.json();
    };

    const fetchMembership = async (dogId) => {
        setMembershipLoading(true);
        setMembershipError('');
        try {
            const data = await membershipApi(`?dogId=${dogId}`);
            // Return the most recent active one first, otherwise the most recent overall
            const sorted = [...data].sort((a, b) => {
                if (a.status === 'active' && b.status !== 'active') return -1;
                if (b.status === 'active' && a.status !== 'active') return 1;
                return new Date(b.startDate) - new Date(a.startDate);
            });
            setDogMembership(sorted[0] || null);
        } catch (err) {
            setMembershipError(err.message);
        } finally {
            setMembershipLoading(false);
        }
    };

    const handleCreateMembership = async () => {
        if (!dogData?._id) return;
        setCreatingMembership(true);
        setMembershipError('');
        try {
            const created = await membershipApi('', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dogId: dogData._id, startDate: new Date(`${newMemberStartDate}T00:00:00`).toISOString() })
            });
            setDogMembership(created);
        } catch (err) {
            setMembershipError(err.message);
        } finally {
            setCreatingMembership(false);
        }
    };

    const membershipPatch = async (path, body) => {
        const updated = await membershipApi(path, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
        setDogMembership(updated);
        return updated;
    };

    const membershipDelete = async (path) => {
        const updated = await membershipApi(path, { method: 'DELETE' });
        setDogMembership(updated);
        return updated;
    };

    // ── Bath package helpers ──
    const bathPkgWrap = async (fn) => {
        setBathPkgBusy(true); setBathPkgError('');
        try {
            const updated = await fn();
            if (updated && updated._id) {
                setBathPackages(prev => prev.map(p => p._id === updated._id ? updated : p));
            }
        } catch (err) {
            setBathPkgError(err.message);
        } finally {
            setBathPkgBusy(false);
        }
    };

    const handleCreateBathPkg = async () => {
        setBathPkgBusy(true); setBathPkgError('');
        try {
            const pkg = await bathPkgHook.create({
                dogId: dogData._id,
                purchaseDate: new Date(`${newBathPkgDate}T00:00:00`).toISOString(),
            });
            setBathPackages(prev => [pkg, ...prev]);
        } catch (err) {
            setBathPkgError(err.message);
        } finally {
            setBathPkgBusy(false);
        }
    };

    // ── Happy Bus package helpers ──
    const happyBusWrap = async (fn) => {
        setHappyBusBusy(true); setHappyBusError('');
        try {
            const updated = await fn();
            if (updated && updated._id) {
                setHappyBusPackages(prev => prev.map(p => p._id === updated._id ? updated : p));
            }
        } catch (err) {
            setHappyBusError(err.message);
        } finally {
            setHappyBusBusy(false);
        }
    };

    const handleCreateHappyBusPkg = async () => {
        setHappyBusBusy(true); setHappyBusError('');
        try {
            const pkg = await happyBusHook.create({
                dogId: dogData._id,
                purchaseDate: new Date(`${newHappyBusPkgDate}T00:00:00`).toISOString(),
            });
            setHappyBusPackages(prev => [pkg, ...prev]);
        } catch (err) {
            setHappyBusError(err.message);
        } finally {
            setHappyBusBusy(false);
        }
    };

    const handleShareCarta = () => {
        const owner = (dogData.owners || [])[0];
        const ownerName = owner?.name || '';
        const rawPhone = (owner?.phone || '').replace(/\D/g, '');
        const dogName = dogData.name || '___________';
        const header = `*Happy Tails — Carta Responsiva*\n\nEstimado/a ${ownerName || '___________'},\nTe compartimos los términos y condiciones de uso de los servicios de Happy Tails para la mascota *${dogName}*. Por favor léelos con atención.\n\n`;
        const body = cartaAsText();
        const text = header + body;
        if (rawPhone) {
            window.open(`https://wa.me/52${rawPhone}?text=${encodeURIComponent(text)}`, '_blank');
        } else {
            navigator.clipboard?.writeText(text).then(() => alert('Carta copiada al portapapeles'));
        }
    };

    const navigateToSection = (section, id = null) => {
        onClose();
        switch (section) {
            case 'daycare':
                navigate('/daycare');
                break;
            case 'hotel':
                navigate('/hotel');
                break;
            case 'vet':
                navigate('/vet');
                break;
            case 'grooming':
                navigate('/grooming');
                break;
            default:
                break;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="dog-details-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <button className="mobile-back-btn" onClick={onClose}>←</button>
                    <div className="header-content">
                        <h2>🐕 {dogData.name}</h2>
                        {dogData.dogId && <span className="dog-id-header">ID: {dogData.dogId}</span>}
                    </div>
                    <div className="mobile-header-actions">
                        {onEdit && (
                            <button
                                className="mobile-action-icon-btn"
                                onClick={() => { onClose(); onEdit(dogData); }}
                                aria-label="Edit dog"
                            >✏️</button>
                        )}
                        {onDelete && (
                            <button
                                className="mobile-action-icon-btn"
                                onClick={() => onDelete(dogData._id)}
                                aria-label="Delete dog"
                            >🗑</button>
                        )}
                    </div>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                {/* Mobile dog hero — hidden on desktop */}
                <div className="dog-hero-mobile">
                    <div className="hero-top-mobile">
                        <div className="dog-avatar-mobile">
                            {dogData.pictureUrl || dogData.picture
                                ? <img src={dogData.pictureUrl || dogData.picture} alt={dogData.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                                : '🐕'}
                        </div>
                        <div className="hero-name-col">
                            <div className="dog-name-mobile">{dogData.name}</div>
                            {dogData.dogId && <div className="dog-id-badge-mobile">ID: {dogData.dogId}</div>}
                        </div>
                    </div>
                    <div className="hero-chips-mobile">
                        {dogData.breed && <span className="chip chip-breed">{dogData.breed}</span>}
                        {(() => {
                            if (dogData.dateOfBirth) {
                                const ageInfo = calculateAgeFromDOB(dogData.dateOfBirth);
                                if (ageInfo) return <span key="age" className="chip chip-age">{ageInfo.displayText}</span>;
                            }
                            if (dogData.age) return <span key="age-legacy" className="chip chip-age">{dogData.age}y</span>;
                            return null;
                        })()}
                        {dogData.weight && (() => {
                            const sizeTag = calculateSizeTag(dogData.weight);
                            return (
                                <React.Fragment key="size-weight">
                                    <span className="chip chip-size">{sizeTag.label}</span>
                                    <span className="chip chip-weight">{dogData.weight} kg</span>
                                </React.Fragment>
                            );
                        })()}
                    </div>
                </div>

                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                <div className="tabs">
                    <button
                        className={`tab ${activeTab === 'info' ? 'active' : ''}`}
                        onClick={() => setActiveTab('info')}
                    >
                        Info
                    </button>
                    <button
                        className={`tab ${activeTab === 'owners' ? 'active' : ''}`}
                        onClick={() => setActiveTab('owners')}
                    >
                        Propietarios
                    </button>
                    <button
                        className={`tab ${activeTab === 'daycare' ? 'active' : ''}`}
                        onClick={() => setActiveTab('daycare')}
                    >
                        Guardería
                    </button>
                    <button
                        className={`tab ${activeTab === 'membership' ? 'active' : ''}`}
                        onClick={() => setActiveTab('membership')}
                    >
                        🏅 Membresía
                    </button>
                    <button
                        className={`tab ${activeTab === 'lunch' ? 'active' : ''}`}
                        onClick={() => setActiveTab('lunch')}
                    >
                        Almuerzo
                    </button>
                    <button
                        className={`tab ${activeTab === 'transportation' ? 'active' : ''}`}
                        onClick={() => setActiveTab('transportation')}
                    >
                        Transporte
                    </button>
                    <button
                        className={`tab ${activeTab === 'hotel' ? 'active' : ''}`}
                        onClick={() => setActiveTab('hotel')}
                    >
                        Hotel
                    </button>
                    <button
                        className={`tab ${activeTab === 'vet' ? 'active' : ''}`}
                        onClick={() => setActiveTab('vet')}
                    >
                        Veterinaria
                    </button>
                    <button
                        className={`tab ${activeTab === 'grooming' ? 'active' : ''}`}
                        onClick={() => setActiveTab('grooming')}
                    >
                        Estética
                    </button>
                    <button
                        className={`tab ${activeTab === 'payments' ? 'active' : ''}`}
                        onClick={() => setActiveTab('payments')}
                    >
                        💳 Pagos
                    </button>
                    <button
                        className={`tab ${activeTab === 'carta' ? 'active' : ''}`}
                        onClick={() => setActiveTab('carta')}
                    >
                        {dogData.responsiveLetter?.ownerSignature ? '✅ Carta' : '📄 Carta'}
                    </button>
                    <button
                        className={`tab ${activeTab === 'pkg-history' ? 'active' : ''}`}
                        onClick={() => setActiveTab('pkg-history')}
                    >
                        📦 Paquetes
                    </button>
                </div>

                <div className="modal-content">
                    {loading && (
                        <div className="loading">Cargando información del perro...</div>
                    )}

                    {activeTab === 'info' && (
                        <div className="tab-content">
                            <h3>🐾 Información básica</h3>
                            
                            {/* Dog Profile Picture */}
                            {(dogData.picture || dogData.pictureUrl) && (
                                <div className="dog-profile-picture-section">
                                    <div className="dog-profile-picture">
                                        <img 
                                            src={dogData.pictureUrl || dogData.picture}
                                            alt={dogData.name || 'Dog'}
                                            className="dog-profile-img"
                                            onError={(e) => {
                                                // Hide image if it fails to load
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                    <p className="profile-picture-label">Foto de perfil</p>
                                </div>
                            )}
                            
                            <div className="info-grid">
                                {dogData.dogId && (
                                    <div className="info-item">
                                        <strong>ID:</strong> {dogData.dogId}
                                    </div>
                                )}
                                <div className="info-item">
                                    <strong>Nombre:</strong> {dogData.name}
                                </div>
                                <div className="info-item">
                                    <strong>Raza:</strong> {dogData.breed}
                                </div>

                                {/* Enhanced Age Display */}
                                <div className="info-item">
                                    <strong>Edad:</strong>
                                    <div className="age-info-display">
                                        {(() => {
                                            const ageInfo = dogData.dateOfBirth ? 
                                                calculateAgeFromDOB(dogData.dateOfBirth) : null;
                                            
                                            if (ageInfo) {
                                                return (
                                                    <>
                                                        <span className="calculated-age">{ageInfo.displayText}</span>
                                                        {ageInfo.isPuppy && (
                                                            <span className="puppy-badge">🐶 Cachorro</span>
                                                        )}
                                                    </>
                                                );
                                            } else if (dogData.age) {
                                                return (
                                                    <span className="legacy-age">
                                                        {dogData.age === 1 ? '1 año' : `${dogData.age} años`}
                                                    </span>
                                                );
                                            } else {
                                                return <span>Desconocida</span>;
                                            }
                                        })()} 
                                    </div>
                                </div>
                                
                                {/* Date of Birth Display */}
                                {dogData.dateOfBirth && (
                                    <div className="info-item">
                                        <strong>Fecha de nacimiento:</strong> {formatDateOnly(dogData.dateOfBirth)}
                                    </div>
                                )}
                                
                                {/* Enhanced Weight Display with Size Tag */}
                                {dogData.weight && (() => {
                                    const sizeTag = calculateSizeTag(dogData.weight);
                                    return (
                                        <div className="info-item weight-item">
                                            <div className="weight-display">
                                                <strong>Peso:</strong> {dogData.weight} kg
                                                <div className="size-tag">
                                                    <span className="size-tag-badge" title={sizeTag.description}>
                                                        {sizeTag.tag}
                                                    </span>
                                                    <span className="size-tag-label">{sizeTag.label}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                            
                            {dogData.particularSigns && (
                                <div className="particular-signs-section">
                                    <h4>🔍 Señas particulares</h4>
                                    <div className="particular-signs-content">
                                        {dogData.particularSigns}
                                    </div>
                                </div>
                            )}
                            
                            {/* Audit Trail */}
                            <div className="audit-section">
                                <AuditLabel
                                    createdBy={dogData.createdBy}
                                    updatedBy={dogData.updatedBy}
                                    createdAt={dogData.createdAt}
                                    updatedAt={dogData.updatedAt}
                                    size="medium"
                                    showBoth={true}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === 'owners' && (
                        <div className="tab-content">
                            <h3>👥 Propietarios</h3>
                            <div className="owners-list">
                                {dogData.owners && dogData.owners.length > 0 ? (
                                    dogData.owners.map((owner, index) => {
                                        const ownerInitials = (owner.name || '').split(' ').filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase();
                                        const phoneClean = (owner.phone || '').replace(/\D/g, '');
                                        return (
                                            <div key={owner._id} className="owner-card">
                                                {/* Mobile: avatar + name + type */}
                                                <div className="owner-top-mobile">
                                                    <div className="owner-av-mobile">{ownerInitials || '?'}</div>
                                                    <div>
                                                        <div className="owner-name-mobile">{owner.name}</div>
                                                        <div className="owner-type-mobile">{index === 0 ? 'Propietario principal' : 'Propietario secundario'}</div>
                                                    </div>
                                                </div>
                                                {/* Desktop-only elements */}
                                                <h4 className="owner-desktop-heading">{index === 0 ? '👤 Propietario principal' : '👥 Propietario secundario'}</h4>
                                                <p className="owner-desktop-name"><strong>Nombre:</strong> {owner.name}</p>
                                                {owner.email && <p className="owner-desktop-email"><strong>Email:</strong> {owner.email}</p>}
                                                {owner.phone && (
                                                    <>
                                                        <p className="owner-desktop-phone"><strong>Teléfono:</strong> {owner.phone}</p>
                                                        {/* Mobile: phone row + action buttons */}
                                                        <div className="owner-phone-row">
                                                            <span className="owner-phone-label">Teléfono</span>
                                                            <span className="owner-phone-value">{owner.phone}</span>
                                                        </div>
                                                        <div className="owner-actions-mobile">
                                                            <a href={`tel:${owner.phone}`} className="act-btn act-call">📞 Llamar</a>
                                                            <a href={`sms:${owner.phone}`} className="act-btn act-sms">💬 SMS</a>
                                                            <a href={`https://wa.me/${phoneClean}`} className="act-btn act-wa" target="_blank" rel="noreferrer">💬 WhatsApp</a>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })
                                ) : (
                                    <p>Sin información de propietarios.</p>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'daycare' && (
                        <div className="tab-content">
                            <div className="section-header">
                                <h3>🏠 Historial de guardería</h3>
                                <button
                                    className="navigate-btn"
                                    onClick={() => navigateToSection('daycare')}
                                >
                                    Ir a guardería →
                                </button>
                            </div>

                            <div className="subsection">
                                <h4>📦 Paquetes de guardería — compras ({daycarePackages.length})</h4>
                                {daycarePackages.length > 0 ? (
                                    <>
                                        {(() => {
                                            const now = new Date();
                                            // Ensure no package is hidden: completed (daysLeft 0 or status completed) takes precedence,
                                            // then ended (expired/cancelled or past expiresAt), rest is active (including missing status)
                                            const completedPkgs = daycarePackages.filter(p => p.status === 'completed' || p.daysLeft === 0);
                                            const endedPkgs = daycarePackages.filter(p => {
                                                const isExpiredByDate = p.expiresAt && new Date(p.expiresAt) < now;
                                                const isEndedStatus = p.status === 'expired' || p.status === 'cancelled';
                                                return (isEndedStatus || isExpiredByDate) && p.status !== 'completed' && p.daysLeft !== 0;
                                            });
                                            const activePkgs = daycarePackages.filter(p => !completedPkgs.includes(p) && !endedPkgs.includes(p));
                                            const pkgCard = (pkg) => {
                                                const statusLabel = pkg.status === 'active' ? `${pkg.daysLeft} días restantes` : pkg.status === 'completed' ? 'Completado' : pkg.status === 'expired' ? 'Vencido' : 'Cancelado';
                                                const statusClass = pkg.status === 'active' ? 'days-left' : pkg.status === 'completed' ? 'days-left days-left--completed' : 'days-left days-left--expired';
                                                return (
                                                    <div key={pkg._id} className={`package-item${pkg.status !== 'active' ? ' package-item--expired' : ''}`}>
                                                        <div className="package-header">
                                                            <strong>{pkg.packageType}</strong>
                                                            <span className={statusClass}>{statusLabel}</span>
                                                        </div>
                                                        <p>Adquirido: {formatDateOnly(pkg.purchasedAt)}</p>
                                                        <p>Vence: {pkg.expiresAt ? formatDateOnly(pkg.expiresAt) : '—'}</p>
                                                        <p>Días restantes: {pkg.daysLeft}</p>
                                                        {pkg.sharedDogs?.length > 0 && <p>Compartido con: {pkg.sharedDogs.length} perro(s)</p>}
                                                    </div>
                                                );
                                            };
                                            return (
                                                <>
                                                    <div style={{ margin: '12px 0 6px', fontWeight: 700, color: '#065f46', fontSize: '0.9rem' }}>🟢 Activos ({activePkgs.length})</div>
                                                    {activePkgs.length ? <div className="packages-list">{activePkgs.map(pkgCard)}</div> : <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>Sin paquetes activos.</p>}
                                                    <div style={{ margin: '14px 0 6px', fontWeight: 700, color: '#1e40af', fontSize: '0.9rem' }}>✅ Completados ({completedPkgs.length})</div>
                                                    {completedPkgs.length ? <div className="packages-list">{completedPkgs.map(pkgCard)}</div> : <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>Sin paquetes completados.</p>}
                                                    <div style={{ margin: '14px 0 6px', fontWeight: 700, color: '#991b1b', fontSize: '0.9rem' }}>⛔ Vencidos / Cancelados ({endedPkgs.length})</div>
                                                    {endedPkgs.length ? <div className="packages-list">{endedPkgs.map(pkgCard)}</div> : <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>Sin paquetes vencidos o cancelados.</p>}
                                                </>
                                            );
                                        })()}
                                    </>
                                ) : (
                                    <p>Sin paquetes de guardería.</p>
                                )}
                            </div>

                            <div className="subsection">
                                <h4>📅 Días en guardería — visitas ({daycareVisits.length})</h4>
                                {daycareVisits.length > 0 && (
                                    <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 10px' }}>
                                        Total días visitados: <strong>{daycareVisits.filter(v => v.checkOutTime).length} completados</strong> · {daycareVisits.filter(v => !v.checkOutTime && v.checkInTime).length} activos
                                    </p>
                                )}
                                {daycareVisits.length > 0 ? (
                                    <div className="visits-list">
                                {(showAllDaycareVisits ? daycareVisits : daycareVisits.slice(0, 5)).map(visit => (
                                            <div key={visit._id} className="visit-item">
                                                <div className="visit-date-pill-mobile">
                                                    {visit.date ? (() => {
                                                        const d = new Date(visit.date);
                                                        return (
                                                            <>
                                                                <span>{d.toLocaleString('es-ES', { month: 'short', timeZone: 'UTC' })}</span>
                                                                <span>{d.getUTCDate()}</span>
                                                            </>
                                                        );
                                                    })() : <span>--</span>}
                                                </div>
                                                <div className="visit-body-mobile">
                                                    <div className="visit-header">
                                                        <strong>{visit.date ? formatDateOnlyUTC(visit.date) : 'Sin fecha'}</strong>
                                                        <span className={`status ${visit.checkOutTime ? 'completed' : 'active'}`}>
                                                            {visit.checkOutTime ? 'Completada' : 'Activa'}
                                                        </span>
                                                    </div>
                                                    <p>Entrada: {visit.checkInTime ? formatTime(visit.checkInTime) : 'Sin entrada'}</p>
                                                    {visit.checkOutTime && (
                                                        <p>Salida: {formatTime(visit.checkOutTime)}</p>
                                                    )}
                                                    {visit.checkoutMood && (() => {
                                                        const m = CHECKOUT_MOODS.find(x => x.value === visit.checkoutMood);
                                                        if (!m) return null;
                                                        return (
                                                            <p style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '6px 0 0' }}>
                                                                <span className="co-mood-badge" style={{ background: m.bg, borderColor: m.border, color: m.color }}>
                                                                    <span className="co-mood-emoji">{m.emoji}</span> {m.label}
                                                                </span>
                                                            </p>
                                                        );
                                                    })()}
                                                    {visit.checkoutBehaviorNotes && <p style={{ fontSize: '0.88rem', color: '#374151', marginTop: 4 }}>Comportamiento: {visit.checkoutBehaviorNotes}</p>}
                                                    {visit.notes && <p>Notas: {visit.notes}</p>}
                                                </div>
                                            </div>
                                        ))}
                                        {daycareVisits.length > 5 && !showAllDaycareVisits && (
                                            <button
                                                onClick={() => setShowAllDaycareVisits(true)}
                                                style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 8, padding: '8px 12px', fontSize: '0.85rem', color: '#6b21a8', cursor: 'pointer', width: '100%' }}
                                            >
                                                Ver todas ({daycareVisits.length} días) →
                                            </button>
                                        )}
                                        {showAllDaycareVisits && daycareVisits.length > 5 && (
                                            <button
                                                onClick={() => setShowAllDaycareVisits(false)}
                                                style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: '0.85rem', color: '#6b7280', cursor: 'pointer', width: '100%' }}
                                            >
                                                Ver menos ↑
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <p>Sin visitas de guardería.</p>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'membership' && (
                        <div className="tab-content">
                            <h3>🏅 Membresía</h3>

                            {membershipLoading && <div className="loading">Cargando membresía…</div>}
                            {membershipError && <div className="error-message">{membershipError}</div>}

                            {!membershipLoading && !dogMembership && (
                                <div className="mem-create-inline">
                                    <p className="mem-create-desc">Este perro no tiene una membresía activa. Crea una para llevar el seguimiento de vacunas, desparasitación y baños.</p>
                                    <div className="mem-create-row">
                                        <label>Fecha de inicio</label>
                                        <input
                                            type="date"
                                            value={newMemberStartDate}
                                            onChange={e => setNewMemberStartDate(e.target.value)}
                                        />
                                    </div>
                                    <button
                                        className="mem-btn-primary"
                                        disabled={creatingMembership}
                                        onClick={handleCreateMembership}
                                    >
                                        {creatingMembership ? 'Creando…' : '+ Crear membresía'}
                                    </button>
                                </div>
                            )}

                            {!membershipLoading && dogMembership && (
                                <MembershipCard
                                    membership={{ ...dogMembership, dog: dogData }}
                                    hideHeader
                                    onApplyVaccine={(id, vaccine, appliedAt) =>
                                        membershipPatch(`/${id}/vaccine`, { vaccine, appliedAt }).catch(e => setMembershipError(e.message))}
                                    onAddDeworming={(id, appliedAt) =>
                                        membershipPatch(`/${id}/deworming`, { appliedAt }).catch(e => setMembershipError(e.message))}
                                    onUpdateDewormingSession={(id, sessionId, appliedAt) =>
                                        membershipPatch(`/${id}/deworming/${sessionId}`, { appliedAt }).catch(e => setMembershipError(e.message))}
                                    onRemoveDeworming={(id, sessionId) =>
                                        membershipDelete(`/${id}/deworming/${sessionId}`).catch(e => setMembershipError(e.message))}
                                    onAddBath={(id, usedAt) =>
                                        membershipPatch(`/${id}/bath`, { usedAt }).catch(e => setMembershipError(e.message))}
                                    onUpdateBathSession={(id, sessionId, appliedAt) =>
                                        membershipPatch(`/${id}/bath/${sessionId}`, { appliedAt }).catch(e => setMembershipError(e.message))}
                                    onRemoveBath={(id, sessionId) =>
                                        membershipDelete(`/${id}/bath/${sessionId}`).catch(e => setMembershipError(e.message))}
                                    onAddVetAppointment={(id, appliedAt) =>
                                        membershipPatch(`/${id}/vet-appointment`, { appliedAt }).catch(e => setMembershipError(e.message))}
                                    onUpdateVetAppointmentSession={(id, sessionId, appliedAt) =>
                                        membershipPatch(`/${id}/vet-appointment/${sessionId}`, { appliedAt }).catch(e => setMembershipError(e.message))}
                                    onRemoveVetAppointment={(id, sessionId) =>
                                        membershipDelete(`/${id}/vet-appointment/${sessionId}`).catch(e => setMembershipError(e.message))}
                                    onApplyMedicalEvent={(id, field, appliedAt) =>
                                        membershipPatch(`/${id}/medical-event`, { field, appliedAt }).catch(e => setMembershipError(e.message))}
                                    onDelete={async (id) => {
                                        if (window.confirm('¿Eliminar esta membresía?')) {
                                            try {
                                                await membershipApi(`/${id}`, { method: 'DELETE' });
                                                setDogMembership(null);
                                            } catch (e) { setMembershipError(e.message); }
                                        }
                                    }}
                                />
                            )}
                        </div>
                    )}

                    {activeTab === 'lunch' && (
                        <div className="tab-content">
                            <h3>🍽️ Instrucciones de Lunch</h3>
                            <div className="extras-form">
                                <div className="form-field">
                                    <label>Instrucciones de alimentación</label>
                                    <textarea
                                        className="lunch-textarea"
                                        value={lunchInstructions}
                                        onChange={e => {
                                            if (e.target.value.length <= 150) {
                                                setLunchInstructions(e.target.value);
                                            }
                                        }}
                                        placeholder="Escriba las instrucciones de lunch para este perro..."
                                        rows={4}
                                        maxLength={150}
                                    />
                                    <div className="char-counter">
                                        <span className={lunchInstructions.length >= 140 ? 'near-limit' : ''}>
                                            {lunchInstructions.length}/150
                                        </span>
                                    </div>
                                </div>
                                <div className="extras-actions">
                                    <button
                                        className="save-extras-btn"
                                        onClick={() => saveExtras('lunch')}
                                        disabled={savingExtras}
                                    >
                                        {savingExtras ? 'Guardando...' : 'Guardar'}
                                    </button>
                                    {extrasSaved === 'lunch' && (
                                        <span className="saved-indicator">✅ Guardado</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'transportation' && (
                        <div className="tab-content">
                            <div className="section-header">
                                <h3>🚐 Paquete Happy Bus</h3>
                            </div>
                            <div className="bp-section">
                                {happyBusError && <p className="bp-error">{happyBusError}</p>}

                                {/* Active package */}
                                {happyBusPackages.filter(p => p.status === 'active').map(pkg => {
                                    const used = pkg.trips?.length || 0;
                                    const total = pkg.totalTrips || 5;
                                    const remaining = total - used;
                                    const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : '';
                                    return (
                                        <div key={pkg._id} className="bp-card">
                                            <div className="bp-card-header">
                                                <span className="bp-tag bp-tag--active">Activo</span>
                                                <span className="bp-progress">{remaining} viaje{remaining !== 1 ? 's' : ''} restante{remaining !== 1 ? 's' : ''} · {used}/{total} usados</span>
                                            </div>
                                            <div className="bp-dots">
                                                {Array.from({ length: total }, (_, i) => {
                                                    const session = pkg.trips?.[i];
                                                    return (
                                                        <div key={i} className={`bp-dot${session ? ' bp-dot--used' : ''}`}>
                                                            <span className="bp-dot-check">{session ? '✓' : ''}</span>
                                                            {session && <span className="bp-dot-date">{fmt(session.usedAt)}</span>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {used < total && (
                                                <div className="bp-add-row">
                                                    <input
                                                        type="date"
                                                        value={addTripDate}
                                                        onChange={e => setAddTripDate(e.target.value)}
                                                        className="bp-date-input"
                                                    />
                                                    <button
                                                        className="bp-btn-add"
                                                        disabled={happyBusBusy}
                                                        onClick={() => happyBusWrap(() => happyBusHook.addTrip(pkg._id, new Date(`${addTripDate}T00:00:00`).toISOString()))}
                                                    >
                                                        + Usar viaje
                                                    </button>
                                                </div>
                                            )}
                                            {used > 0 && (
                                                <button
                                                    className="bp-btn-undo"
                                                    disabled={happyBusBusy}
                                                    onClick={() => {
                                                        const last = pkg.trips?.[pkg.trips.length - 1];
                                                        if (last) happyBusWrap(() => happyBusHook.removeTrip(pkg._id, last._id));
                                                    }}
                                                >
                                                    Deshacer último viaje
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* No active package — show create form */}
                                {happyBusPackages.filter(p => p.status === 'active').length === 0 && (
                                    <div className="bp-create">
                                        <p className="bp-create-desc">Sin paquete activo. Crear uno nuevo (5 viajes, sin vencimiento):</p>
                                        <div className="bp-create-row">
                                            <label>Fecha de compra</label>
                                            <input
                                                type="date"
                                                value={newHappyBusPkgDate}
                                                onChange={e => setNewHappyBusPkgDate(e.target.value)}
                                                className="bp-date-input"
                                            />
                                        </div>
                                        <button className="bp-btn-create" disabled={happyBusBusy} onClick={handleCreateHappyBusPkg}>
                                            {happyBusBusy ? 'Creando…' : '+ Crear paquete Happy Bus'}
                                        </button>
                                    </div>
                                )}

                                {/* Completed packages history */}
                                {happyBusPackages.filter(p => p.status === 'completed').length > 0 && (
                                    <div className="bp-completed-list">
                                        <p style={{ fontSize: '.8rem', fontWeight: 700, color: '#8b93a7', margin: '16px 0 8px', textTransform: 'uppercase', letterSpacing: '.08em' }}>Paquetes anteriores</p>
                                        {happyBusPackages.filter(p => p.status === 'completed').slice(0, 3).map(pkg => (
                                            <div key={pkg._id} className="bp-card bp-card--done">
                                                <div className="bp-card-header">
                                                    <span className="bp-tag bp-tag--done">Completado</span>
                                                    <span className="bp-progress">{pkg.trips?.length}/{pkg.totalTrips} viajes</span>
                                                </div>
                                                <div className="bp-dots">
                                                    {Array.from({ length: pkg.totalTrips || 5 }, (_, i) => (
                                                        <div key={i} className="bp-dot bp-dot--used"><span className="bp-dot-check">✓</span></div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'hotel' && (
                        <div className="tab-content">
                            <div className="section-header">
                                <h3>🏨 Estadías en hotel</h3>
                                <button
                                    className="navigate-btn"
                                    onClick={() => navigateToSection('hotel')}
                                >
                                    Ir a hotel →
                                </button>
                            </div>

                            {hotelStays.length > 0 ? (
                                <div className="hotel-stays-list">
                                    {hotelStays.map(stay => (
                                        <div key={stay._id} className="hotel-stay-item">
                                            <div className="stay-header">
                                                <strong>{formatDateOnly(stay.checkIn)} - {formatDateOnly(stay.checkOut)}</strong>
                                                <span className={`status ${stay.status}`}>{stay.status}</span>
                                            </div>

                                            {/* Intake form link */}
                                            {stay.intakeForm?.submittedAt ? (
                                                <div className="intake-form-badge">
                                                    <span>📋 Formulario completado</span>
                                                    <button
                                                        className="intake-view-btn"
                                                        onClick={() => navigate(`/hotel/intake/${stay._id}/staff`)}
                                                    >
                                                        Ver →
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="intake-form-badge intake-form-badge--empty">
                                                    <span>📋 Sin formulario de ingreso</span>
                                                    <button
                                                        className="intake-view-btn"
                                                        onClick={() => navigate(`/hotel/intake/${stay._id}/staff`)}
                                                    >
                                                        Completar →
                                                    </button>
                                                </div>
                                            )}

                                            {stay.vetCheckIn && (
                                                <div className="vet-info">
                                                    <h5>📋 Examen de entrada</h5>
                                                    {stay.vetCheckIn.weight && <p>Peso: {stay.vetCheckIn.weight} kg</p>}
                                                    {stay.vetCheckIn.temperature && <p>Temperatura: {stay.vetCheckIn.temperature}°C</p>}
                                                    {stay.vetCheckIn.notes && <p>Notas: {stay.vetCheckIn.notes}</p>}
                                                </div>
                                            )}

                                            {stay.vetCheckOut && (
                                                <div className="vet-info">
                                                    <h5>📋 Examen de salida</h5>
                                                    {stay.vetCheckOut.weight && <p>Peso: {stay.vetCheckOut.weight} kg</p>}
                                                    {stay.vetCheckOut.temperature && <p>Temperatura: {stay.vetCheckOut.temperature}°C</p>}
                                                    {stay.vetCheckOut.notes && <p>Notas: {stay.vetCheckOut.notes}</p>}
                                                </div>
                                            )}

                                            {stay.notes && <p><strong>Notas generales:</strong> {stay.notes}</p>}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p>Sin estadías en hotel.</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'vet' && (
                        <div className="tab-content">
                            <div className="section-header">
                                <h3>🩺 Citas veterinarias</h3>
                                <button
                                    className="navigate-btn"
                                    onClick={() => navigateToSection('vet')}
                                >
                                    Ir a veterinaria →
                                </button>
                            </div>

                            {vetAppointments.length > 0 ? (
                                <div className="simple-appointments-list">
                                    {vetAppointments.slice(0, 8).map(appointment => {
                                        const appointmentDate = appointment.appointmentDate || appointment.date;
                                        const formattedDate = formatDate(appointmentDate);

                                        return (
                                            <div key={appointment._id} className="simple-appointment-item">
                                                <div className="simple-appointment-header">
                                                    <span className="simple-date">{formattedDate}</span>
                                                    <span className={`simple-status ${appointment.status || 'scheduled'}`}>
                                                        {(appointment.status || 'scheduled').replace('-', ' ')}
                                                    </span>
                                                </div>

                                                <div className="simple-appointment-content">
                                                    <p><strong>Motivo:</strong> {appointment.cause || appointment.reason || 'Sin motivo'}</p>

                                                    {(appointment.temperature || appointment.weight) && (
                                                        <div className="simple-metrics">
                                                            {appointment.temperature && (
                                                                <span>Temp: {appointment.temperature}°C</span>
                                                            )}
                                                            {appointment.weight && (
                                                                <span>Peso: {appointment.weight} kg</span>
                                                            )}
                                                        </div>
                                                    )}

                                                    {appointment.findings && (
                                                        <p><strong>Hallazgos:</strong> {appointment.findings}</p>
                                                    )}

                                                    {appointment.recommendations && (
                                                        <p><strong>Tratamiento:</strong> {appointment.recommendations}</p>
                                                    )}

                                                    {appointment.vetNotes && (
                                                        <p><strong>Notas:</strong> {appointment.vetNotes}</p>
                                                    )}

                                                    {appointment.followUpRequired && (
                                                        <p className="follow-up-note">
                                                            <strong>ℹ️ Seguimiento necesario</strong>
                                                            {appointment.followUpDate && (
                                                                <span> - {formatDateOnly(appointment.followUpDate)}</span>
                                                            )}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {vetAppointments.length > 8 && (
                                        <p className="more-items">
                                            ... y {vetAppointments.length - 8} citas más
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <p>Sin citas veterinarias.</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'grooming' && (
                        <div className="tab-content">

                            {/* ── Bath Package ── */}
                            <div className="bp-section">
                                <div className="section-header">
                                    <h3>🛁 Paquete de baños</h3>
                                </div>
                                {bathPkgError && <p className="bp-error">{bathPkgError}</p>}

                                {bathPackages.filter(p => p.status === 'active').map(pkg => {
                                    const used = pkg.baths?.length || 0;
                                    const total = pkg.totalBaths || 5;
                                    const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : '';
                                    return (
                                        <div key={pkg._id} className="bp-card">
                                            <div className="bp-card-header">
                                                <span className="bp-tag bp-tag--active">Activo</span>
                                                <span className="bp-progress">{used}/{total} baños usados</span>
                                            </div>
                                            <div className="bp-dots">
                                                {Array.from({ length: total }, (_, i) => {
                                                    const session = pkg.baths?.[i];
                                                    return (
                                                        <div key={i} className={`bp-dot${session ? ' bp-dot--used' : ''}`}>
                                                            <span className="bp-dot-check">{session ? '✓' : ''}</span>
                                                            {session && <span className="bp-dot-date">{fmt(session.usedAt)}</span>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {used < total && (
                                                <div className="bp-add-row">
                                                    <input type="date" value={addBathDate} onChange={e => setAddBathDate(e.target.value)} className="bp-date-input" />
                                                    <button
                                                        className="bp-btn-add"
                                                        disabled={bathPkgBusy}
                                                        onClick={() => bathPkgWrap(() => bathPkgHook.addBath(pkg._id, new Date(`${addBathDate}T00:00:00`).toISOString()))}
                                                    >
                                                        + Marcar baño
                                                    </button>
                                                </div>
                                            )}
                                            {used > 0 && (
                                                <button
                                                    className="bp-btn-undo"
                                                    disabled={bathPkgBusy}
                                                    onClick={() => {
                                                        const last = pkg.baths?.[pkg.baths.length - 1];
                                                        if (last) bathPkgWrap(() => bathPkgHook.removeBath(pkg._id, last._id));
                                                    }}
                                                >
                                                    Deshacer último baño
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}

                                {bathPackages.filter(p => p.status === 'completed').length > 0 && (
                                    <div className="bp-completed-list">
                                        {bathPackages.filter(p => p.status === 'completed').slice(0, 3).map(pkg => (
                                            <div key={pkg._id} className="bp-card bp-card--done">
                                                <div className="bp-card-header">
                                                    <span className="bp-tag bp-tag--done">Completado</span>
                                                    <span className="bp-progress">{pkg.baths?.length}/{pkg.totalBaths} baños</span>
                                                </div>
                                                <div className="bp-dots">
                                                    {Array.from({ length: pkg.totalBaths || 5 }, (_, i) => (
                                                        <div key={i} className="bp-dot bp-dot--used"><span className="bp-dot-check">✓</span></div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {bathPackages.filter(p => p.status === 'active').length === 0 && (
                                    <div className="bp-create">
                                        <p className="bp-create-desc">Sin paquete activo. Crear uno nuevo:</p>
                                        <div className="bp-create-row">
                                            <label>Fecha de compra</label>
                                            <input type="date" value={newBathPkgDate} onChange={e => setNewBathPkgDate(e.target.value)} className="bp-date-input" />
                                        </div>
                                        <button className="bp-btn-create" disabled={bathPkgBusy} onClick={handleCreateBathPkg}>
                                            {bathPkgBusy ? 'Creando…' : '+ Crear paquete de baños'}
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="section-header" style={{ marginTop: 20 }}>
                                <h3>✂️ Historial de estética</h3>
                                <button
                                    className="navigate-btn"
                                    onClick={() => navigateToSection('grooming')}
                                >
                                    Ir a estética →
                                </button>
                            </div>

                            {groomingBookings.length > 0 ? (
                                <div className="grooming-bookings-list">
                                    {groomingBookings.slice(0, 8).map(booking => {
                                        const appointmentDate = booking.appointmentDate;
                                        const formattedDate = formatDate(appointmentDate);

                                        const formatServices = (services) => {
                                            if (!services || services.length === 0) return 'Sin servicios';
                                            return services.map(service => {
                                                switch (service) {
                                                    case 'full-grooming': return 'Estética completa';
                                                    case 'bath': return 'Baño';
                                                    case 'haircut': return 'Corte';
                                                    case 'nail-trim': return 'Corte de uñas';
                                                    case 'ear-cleaning': return 'Limpieza de oídos';
                                                    case 'teeth-cleaning': return 'Limpieza dental';
                                                    default: return service;
                                                }
                                            }).join(', ');
                                        };

                                        return (
                                            <div key={booking._id} className="simple-appointment-item">
                                                <div className="simple-appointment-header">
                                                    <span className="simple-date">{formattedDate}</span>
                                                    <span className={`simple-status ${booking.status || 'scheduled'}`}>
                                                        {(booking.status || 'scheduled').replace('-', ' ')}
                                                    </span>
                                                </div>
                                                <div className="simple-appointment-content">
                                                    <p><strong>Servicios:</strong> {formatServices(booking.groomingServices)}</p>
                                                    <p><strong>Peso:</strong> {booking.weight} kg</p>
                                                    <p><strong>Teléfono:</strong> {booking.ownerPhone}</p>
                                                    {booking.notes && (
                                                        <p><strong>Notas:</strong> {booking.notes}</p>
                                                    )}
                                                    {booking.groomingNotes && (
                                                        <p><strong>Notas de estética:</strong> {booking.groomingNotes}</p>
                                                    )}
                                                    {booking.completedAt && (
                                                        <p className="completed-note">
                                                            <strong>✅ Completado:</strong> {formatDate(booking.completedAt)}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {groomingBookings.length > 8 && (
                                        <p className="more-items">
                                            ... y {groomingBookings.length - 8} sesiones más
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <p>Sin citas de estética.</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'payments' && (
                        <div className="tab-content">
                            <div className="section-header">
                                <h3>💳 Ventas y pagos</h3>
                                <button
                                    className="navigate-btn"
                                    onClick={() => { onClose(); navigate('/pos'); }}
                                >
                                    Ir a ventas →
                                </button>
                            </div>

                            {dogSales.length > 0 ? (
                                <>
                                    <div className="ddm-sales-summary">
                                        <div className="ddm-stat-card">
                                            <span className="ddm-stat-label">Ventas</span>
                                            <span className="ddm-stat-value">{dogSales.length}</span>
                                        </div>
                                        <div className="ddm-stat-card">
                                            <span className="ddm-stat-label">Cobrado</span>
                                            <span className="ddm-stat-value">
                                                {ILS(dogSales.reduce((sum, s) => sum + (s.payments || []).reduce((ps, p) => ps + (p.amount || 0), 0), 0))}
                                            </span>
                                        </div>
                                        <div className="ddm-stat-card">
                                            <span className="ddm-stat-label">Por cobrar</span>
                                            <span className="ddm-stat-value ddm-stat-due">
                                                {ILS(dogSales.filter(s => s.status === 'partial' || s.status === 'due').reduce((sum, s) => sum + (s.remaining || 0), 0))}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="ddm-sales-list">
                                        {dogSales.map(sale => {
                                            const payIcons = { cash: '💵', card: '💳', bit: '📲', transfer: '🏦' };
                                            const statusClass = {
                                                paid: 'ddm-st-paid',
                                                partial: 'ddm-st-partial',
                                                due: 'ddm-st-due',
                                                draft: 'ddm-st-draft',
                                            }[sale.status] || 'ddm-st-draft';

                                            return (
                                                <div
                                                    key={sale._id}
                                                    className="ddm-sale-item"
                                                    onClick={() => { onClose(); navigate(`/pos/${sale._id}`); }}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={e => e.key === 'Enter' && (onClose(), navigate(`/pos/${sale._id}`))}
                                                >
                                                    <div className="ddm-sale-header">
                                                        <div className="ddm-sale-id-date">
                                                            <span className="ddm-sale-id">{sale.saleNumber ? `#${String(sale.saleNumber).padStart(4, '0')}` : `#${sale._id?.slice(-6).toUpperCase()}`}</span>
                                                            <span className="ddm-sale-date">{formatRelativeDate(sale.createdAt)}</span>
                                                        </div>
                                                        <div className="ddm-sale-right">
                                                            <span className="ddm-sale-total">{ILS(sale.total || 0)}</span>
                                                            <span className={`ddm-status-pill ${statusClass}`}>
                                                                {sale.status === 'paid' ? 'Pagado'
                                                                    : sale.status === 'partial' ? 'Parcial'
                                                                    : sale.status === 'due' ? 'Pendiente'
                                                                    : 'Borrador'}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {sale.items && sale.items.length > 0 && (
                                                        <p className="ddm-sale-items-txt">
                                                            {sale.items.map(it => it.name || it.description).filter(Boolean).join(' · ')}
                                                        </p>
                                                    )}

                                                    {sale.payments && sale.payments.length > 0 && (
                                                        <>
                                                            <span className="ddm-payments-label">Pagos</span>
                                                            <div className="ddm-pay-rows">
                                                                {sale.payments.map((pay, i) => {
                                                                    const method = (pay.method || 'cash').toLowerCase();
                                                                    return (
                                                                        <div key={i} className="ddm-pay-row">
                                                                            <span className={`ddm-pay-icon ${method}`}>
                                                                                {payIcons[method] || '💳'}
                                                                            </span>
                                                                            <span className="ddm-pay-method">{pay.method}</span>
                                                                            <span className="ddm-pay-date">{pay.when ? formatRelativeDate(pay.when) : ''}</span>
                                                                            <span className="ddm-pay-amt">{ILS(pay.amount || 0)}</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </>
                                                    )}

                                                    {sale.status === 'partial' && sale.remaining > 0 && (
                                                        <div className="ddm-sale-remaining">
                                                            Pendiente: {ILS(sale.remaining)}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            ) : (
                                <div className="ddm-empty-payments">
                                    <p>Sin ventas registradas para este perro.</p>
                                    <button
                                        className="ddm-new-sale-btn"
                                        onClick={() => { onClose(); navigate(`/pos/new?dogId=${dogData._id}`); }}
                                    >
                                        + Nueva venta
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {activeTab === 'carta' && (
                        <div className="tab-content">
                            {dogData.responsiveLetter?.ownerSignature ? (
                                /* ── SIGNED STATE ── */
                                <div className="carta-signed-wrapper">
                                    <div className="carta-signed-badge">
                                        <span className="carta-signed-icon">✅</span>
                                        <div>
                                            <div className="carta-signed-title">Carta Responsiva firmada</div>
                                            {dogData.responsiveLetter?.signedAt && (
                                                <div className="carta-signed-date">
                                                    Firmada el {new Date(dogData.responsiveLetter.signedAt).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="carta-sig-review">
                                        <p className="carta-sig-label">Firma del propietario</p>
                                        <img src={dogData.responsiveLetter.ownerSignature} alt="Firma" className="carta-sig-img" />
                                    </div>

                                    {/* Scrollable letter for review */}
                                    <details className="carta-review-details">
                                        <summary className="carta-review-summary">Ver términos y condiciones</summary>
                                        <div className="carta-scroll carta-scroll--review">
                                            <div className="df-carta-header">
                                                <div className="df-carta-logo-line">🐾 Happy Tails</div>
                                                <h3 className="df-carta-title">Carta Responsiva</h3>
                                                <p className="df-carta-intro">{CARTA_INTRO}</p>
                                            </div>
                                            <ol className="df-carta-list">
                                                {CARTA_CLAUSES.map((clause, i) => (
                                                    <li key={i} className="df-carta-item">
                                                        {clause.text}
                                                        {clause.bullets && (
                                                            <ul className="df-carta-sublist">
                                                                {clause.bullets.map((bullet, j) => (
                                                                    <li key={j}>{bullet}</li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </li>
                                                ))}
                                            </ol>
                                        </div>
                                    </details>

                                    <div className="carta-actions">
                                        <button className="carta-whatsapp-btn" onClick={handleShareCarta}>
                                            <span>💬</span> Compartir por WhatsApp
                                        </button>
                                        <button className="carta-resign-btn" onClick={() => setDogData(d => ({ ...d, responsiveLetter: { ...d.responsiveLetter, ownerSignature: '', signedAt: null } }))}>
                                            Volver a firmar
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                /* ── UNSIGNED STATE ── */
                                <div className="carta-unsigned-wrapper">
                                    <div className="carta-pending-badge">
                                        <span>📄</span>
                                        <span>Carta pendiente de firma</span>
                                    </div>

                                    <div className="carta-scroll">
                                        <div className="df-carta-header">
                                            <div className="df-carta-logo-line">🐾 Happy Tails</div>
                                            <h3 className="df-carta-title">Carta Responsiva</h3>
                                            <p className="df-carta-intro">{CARTA_INTRO}</p>
                                        </div>
                                        <ol className="df-carta-list">
                                            {CARTA_CLAUSES.map((clause, i) => (
                                                <li key={i} className="df-carta-item">
                                                    {clause.text}
                                                    {clause.bullets && (
                                                        <ul className="df-carta-sublist">
                                                            {clause.bullets.map((bullet, j) => (
                                                                <li key={j}>{bullet}</li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </li>
                                            ))}
                                        </ol>
                                    </div>

                                    <div className="carta-sig-block">
                                        <p className="carta-sig-label">Firma del propietario</p>
                                        {cartaSignature ? (
                                            <div className="carta-sig-preview">
                                                <img src={cartaSignature} alt="Firma" className="carta-sig-img" />
                                                <button type="button" className="carta-clear-btn" onClick={() => setCartaSignature('')}>
                                                    Volver a firmar
                                                </button>
                                            </div>
                                        ) : (
                                            <SignaturePad onChange={sig => setCartaSignature(sig)} />
                                        )}
                                    </div>

                                    {savingCarta === 'error' && (
                                        <div className="carta-msg carta-msg--error">Error al guardar. Intenta de nuevo.</div>
                                    )}
                                    {savingCarta === 'saved' && (
                                        <div className="carta-msg carta-msg--success">¡Firma guardada correctamente!</div>
                                    )}

                                    <div className="carta-actions">
                                        <button
                                            className="carta-save-btn"
                                            disabled={!cartaSignature || savingCarta === 'saving'}
                                            onClick={() => saveCarta(cartaSignature)}
                                        >
                                            {savingCarta === 'saving' ? 'Guardando...' : '💾 Guardar firma'}
                                        </button>
                                        <button className="carta-whatsapp-btn" onClick={handleShareCarta}>
                                            <span>💬</span> Compartir por WhatsApp
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {activeTab === 'pkg-history' && (
                        <div className="tab-content">
                            <h3>📦 Historial de paquetes</h3>
                            {packageHistory.length === 0 ? (
                                <p>Sin eventos de paquetes registrados.</p>
                            ) : (
                                <div className="pkg-history-timeline">
                                    {packageHistory.map(event => {
                                        const actionLabels = {
                                            created: { label: 'Creado', cls: 'ph-created' },
                                            updated: { label: 'Editado', cls: 'ph-updated' },
                                            used:    { label: 'Usado',   cls: 'ph-used' },
                                            completed: { label: 'Completado', cls: 'ph-completed' },
                                            cancelled: { label: 'Cancelado',  cls: 'ph-cancelled' },
                                            deleted:   { label: 'Eliminado',  cls: 'ph-deleted' },
                                        };
                                        const a = actionLabels[event.action] || { label: event.action, cls: 'ph-other' };
                                        return (
                                            <div key={event._id} className="pkg-history-event">
                                                <div className="ph-left">
                                                    <span className={`ph-badge ${a.cls}`}>{a.label}</span>
                                                </div>
                                                <div className="ph-body">
                                                    <div className="ph-top-row">
                                                        <span className="ph-type">{event.packageType}</span>
                                                        {event.daysLeftBefore !== undefined && event.daysLeftAfter !== undefined && (
                                                            <span className="ph-days">
                                                                {event.daysLeftBefore} → {event.daysLeftAfter} días
                                                            </span>
                                                        )}
                                                        {event.daysLeftBefore === undefined && event.daysLeftAfter !== undefined && (
                                                            <span className="ph-days">{event.daysLeftAfter} días</span>
                                                        )}
                                                    </div>
                                                    {event.note && <p className="ph-note">{event.note}</p>}
                                                    <div className="ph-meta">
                                                        <span className="ph-date">{formatDate(event.createdAt)}</span>
                                                        {event.performedBy?.username && (
                                                            <span className="ph-user">· {event.performedBy.username}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}